import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resolveImageUrl } from '../lib/imageUrl';
import { motion } from 'framer-motion';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { t, getLocalizedContent } = useLanguage();
  const { items, removeItem, updateQuantity, clearCart, totalPrice } = useCart();
  const navigate = useNavigate();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/quote-requests`, {
        ...formData,
        items: items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        timestamp: Math.floor(Date.now() / 1000)
      });
      
      toast.success('Demande de devis envoyée avec succès !');
      clearCart();
      setShowQuoteForm(false);
      navigate('/');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  if (items.length === 0 && !showQuoteForm) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4" data-testid="cart-empty">
        <ShoppingCart className="h-24 w-24 text-gray-300 mb-6" />
        <h2 className="text-2xl font-bold text-[#0F172A] mb-2">{t('cart_empty')}</h2>
        <p className="text-gray-500 mb-6">Découvrez nos produits et ajoutez-les à votre panier</p>
        <Link to="/shop">
          <Button className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à la boutique
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page">
      {/* Header */}
      <section className="py-12 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Montserrat']">
            {showQuoteForm ? t('quote_title') : t('cart_title')}
          </h1>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!showQuoteForm ? (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map((item, index) => (
                  <motion.div
                    key={item.product.id}
                    initial="hidden"
                    animate="visible"
                    variants={fadeInUp}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="border-0 shadow-sm" data-testid={`cart-item-${index}`}>
                      <CardContent className="p-4">
                        <div className="flex gap-4">
                          <img
                            src={resolveImageUrl(item.product.images?.[0]) || 'https://via.placeholder.com/100'}
                            alt={getLocalizedContent(item.product.name)}
                            className="w-24 h-24 object-cover rounded-lg"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-[#0F172A]">
                              {getLocalizedContent(item.product.name)}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                              {item.product.price?.toLocaleString()} {t('common_fcfa')} / unité
                            </p>
                            <div className="flex items-center justify-between mt-4">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                                  data-testid={`decrease-${index}`}
                                >
                                  <Minus className="h-4 w-4" />
                                </Button>
                                <span className="w-8 text-center font-medium">{item.quantity}</span>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                                  data-testid={`increase-${index}`}
                                >
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="font-bold text-[#0B6AB3]">
                                  {(item.product.price * item.quantity).toLocaleString()} {t('common_fcfa')}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeItem(item.product.id)}
                                  className="text-red-500 hover:text-red-700"
                                  data-testid={`remove-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Summary */}
              <div>
                <Card className="border-0 shadow-sm sticky top-24" data-testid="cart-summary">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg text-[#0F172A] mb-4">Résumé</h3>
                    <div className="space-y-3 pb-4 border-b">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Sous-total</span>
                        <span>{totalPrice.toLocaleString()} {t('common_fcfa')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Livraison</span>
                        <span>À définir</span>
                      </div>
                    </div>
                    <div className="flex justify-between py-4 font-bold text-lg">
                      <span>{t('cart_total')}</span>
                      <span className="text-[#0B6AB3]">{totalPrice.toLocaleString()} {t('common_fcfa')}</span>
                    </div>
                    <Button
                      className="w-full bg-[#E50E24] hover:bg-[#c40c1f] text-white"
                      onClick={() => setShowQuoteForm(true)}
                      data-testid="checkout-btn"
                    >
                      {t('cart_checkout')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                    <p className="text-xs text-gray-500 text-center mt-4">
                      Paiement à la livraison uniquement
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            /* Quote Form */
            <div className="max-w-2xl mx-auto">
              <Button
                variant="ghost"
                onClick={() => setShowQuoteForm(false)}
                className="mb-6"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Retour au panier
              </Button>
              
              <Card className="border-0 shadow-lg" data-testid="quote-form">
                <CardContent className="p-6 md:p-8">
                  <h2 className="text-2xl font-bold text-[#0F172A] mb-6 font-['Montserrat']">
                    {t('quote_title')}
                  </h2>
                  
                  {/* Order Summary */}
                  <div className="mb-6 p-4 bg-[#F8F9FA] rounded-lg">
                    <h4 className="font-medium mb-2">Votre commande:</h4>
                    {items.map((item) => (
                      <div key={item.product.id} className="flex justify-between text-sm py-1">
                        <span>{getLocalizedContent(item.product.name)} x{item.quantity}</span>
                        <span>{(item.product.price * item.quantity).toLocaleString()} FCFA</span>
                      </div>
                    ))}
                    <div className="flex justify-between font-bold mt-2 pt-2 border-t">
                      <span>Total</span>
                      <span>{totalPrice.toLocaleString()} FCFA</span>
                    </div>
                  </div>

                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">{t('contact_name')} *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          data-testid="quote-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company">{t('quote_company')}</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                          data-testid="quote-company"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">{t('contact_email')} *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="quote-email"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">{t('contact_phone')} *</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          data-testid="quote-phone"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="message">{t('contact_message')}</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        rows={4}
                        placeholder="Informations complémentaires (adresse de livraison, etc.)"
                        data-testid="quote-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
                      disabled={submitting}
                      data-testid="quote-submit"
                    >
                      {submitting ? 'Envoi en cours...' : 'Envoyer la demande de devis'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
